import { Close } from "@carbon/icons-react";
import cx from "classnames";
import type { FC, ReactElement, ReactNode } from "react";
import React from "react";
import { match } from "ts-pattern";

import { Button } from "../Button";
import { ForceTheme, useTheme } from "../ThemeProvider";

export type BannerProps = {
	variant?: "positive" | "negative" | "info";
	level?: "primary" | "secondary";
	dismissible?: boolean;
	onDismiss?: (e?: any) => any;
	icon?: ReactElement;
	children?: ReactNode;
	title?: string;
	message?: ReactNode;
	shouldShowAction?: boolean;
	actionOnClick?: () => void;
	actionText?: string;
};

export const Banner: FC<BannerProps> = ({
	variant = "info",
	level = "primary",
	dismissible = false,
	onDismiss,
	icon,
	title = "",
	message = "",
	shouldShowAction = false,
	actionOnClick,
	actionText = "",
}) => {
	const { resolvedTheme } = useTheme();

	const linkStyles = [
		"[&_a]:[color:inherit]",
		"[&_a]:underline",
		"[&_a:hover]:no-underline",
		"[&_a:focus]:rounded",
		"[&_a:focus]:ring",
		"[&_a:focus]:[--tw-ring-color:var(--text-inverse-disable)]",
		"[&_button]:[color:inherit]",
		"[&_button]:underline",
		"[&_button:hover]:no-underline",
		"[&_button:focus]:rounded",
		"[&_button:focus]:ring",
		"[&_button:focus]:[--tw-ring-color:var(--text-inverse-disable)]",
	];

	const variantStyles = match({ variant, level, resolvedTheme })
		.with({ variant: "info", level: "primary" }, () => [
			"bg-inverse-primary",
			"text-fixed-light-default",
		])
		.with({ variant: "info", level: "secondary" }, () => [
			"bg-warning-light",
			"text-primary",
			"shadow-border-bottom-warning-mid",
		])
		.with({ variant: "positive", level: "primary" }, () => [
			"bg-positive",
			"text-fixed-light-default",
		])
		.with({ variant: "positive", level: "secondary" }, () => [
			"bg-positive-light",
			"shadow-border-bottom-positive-mid",
		])
		.with({ variant: "negative", level: "primary" }, () => [
			"bg-negative",
			"text-fixed-light-default",
		])
		.with({ variant: "negative", level: "secondary", resolvedTheme: "light" }, () => [
			"bg-negative-light",
			"shadow-border-bottom-negative-mid",
		])
		.with({ variant: "negative", level: "secondary", resolvedTheme: "dark" }, () => [
			"bg-negative-light",
			"shadow-border-bottom-negative-mid",
		])
		// Fallback style
		.otherwise(() => ["bg-inverse-primary", "text-fixed-light-default"]);

	const BannerIcon = icon
		? React.cloneElement(icon as any, { className: "inline-block -mt-1 mr-1" })
		: null;

	return (
		<div
			className={cx(
				"w-full",
				"flex",
				"items-center",
				"justify-center",
				"py-2",
				"px-3",
				"text-sm",
				"sticky",
				"top-0",
				"left-0",
				"space-x-2",
				variantStyles
			)}
		>
			{dismissible && <div className="h-6 w-6 shrink-0"></div>}
			<div
				className={cx(
					"grow",
					"leading-4",
					"items-center",
					"py-1",
					"text-center",
					"mx-auto",
					"justify-center",
					linkStyles
				)}
			>
				{BannerIcon}
				{title && <span className="font-bold">{title}</span>} {message}{" "}
				{shouldShowAction && (
					<button className="link--buttonish" onClick={actionOnClick}>
						{actionText}
					</button>
				)}
			</div>
			{dismissible && (
				<ForceTheme
					theme={level === "primary" || resolvedTheme === "dark" ? "dark" : "light"}
				>
					<Button
						level="tertiary"
						size="xs"
						aria-label="Close banner"
						onClick={(e) => onDismiss?.(e)}
						icon={Close}
					/>
				</ForceTheme>
			)}
		</div>
	);
};
